// CORE IMPORTS
import { useMemo } from "react";

// TYPES
import { ICurrency } from "../features/trade/targets/components/CryptoSelectionModalV2";

// REDUX
import api from "src/store/features/api";

// CONSTANTS
import { STABLE_COIN_CODES } from "../features/trade/constants";

import { isTokenRestrictedForCountry } from "src/utils/restrictedCountry";

export const useCurrencyData = (userBaseCurrency: string) => {
  const { data: userData } = api.useGetUserDatav2Query();
  const userCountry = userData?.address?.country;

  const {
    data: userHoldings = [],
    isLoading: isHoldingsLoading,
    isFetching: isHoldingsFetching,
    isError: isHoldingsError,
    refetch: refetchWallet,
  } = api.useGetWalletPageHoldingsNewQuery({
    baseCurrency: userBaseCurrency,
  });

  const {
    data: cryptoAll = [],
    isLoading: isCryptoAllLoading,
    isError: isCryptoAllError,
  } = api.useGetCryptoAllQuery({
    quote: userBaseCurrency,
  });

  const {
    data: forexAll = [],
    isLoading: isFiatLoading,
    isError: isFiatError,
  } = api.useGetFiatCurrenciesQuery({
    quote: userBaseCurrency,
  });

  /**
   * Memoized array of user's crypto holdings filtered by permissible currencies and formatted as ICurrency objects.
   */
  const usersCryptoHoldings = useMemo((): ICurrency[] => {
    if (!userHoldings) return [];

    // remove user holdings that DO NOT have buy or sell permissions
    const permissibleHoldings = userHoldings.filter((currency) => {
      return (
        currency.currencyConfig?.permissions?.sell && currency.currencyConfig?.type === "Crypto"
      );
    });

    // map through permissible holdings and format as ICurrency objects
    return permissibleHoldings.map((currency): ICurrency => {
      const config = currency.currencyConfig;
      return {
        userBaseCurrency: userBaseCurrency,
        code: config.code ?? "",
        displayName: config.displayName ?? "",
        logoURL: config.logoUrl ?? "",
        units: currency.available.toString(),
        value: currency.value.toString(),
        rank: "",
        sector: "",
        price: currency.price,
      };
    });
  }, [userHoldings, userBaseCurrency]);

  /**
   * Memoized array of user's crypto holdings filtered by permissible currencies and formatted as ICurrency objects.
   * Excludes stablecoins.
   */
  const userCryptoHoldingsWithoutStables = useMemo((): ICurrency[] => {
    if (!userHoldings) return [];

    // remove user holdings that DO NOT have buy or sell permissions
    const permissibleHoldings = userHoldings.filter((currency) => {
      const isStableCoin =
        currency.currencyConfig?.primarySector === "Stablecoin" ||
        currency.currencyConfig?.secondarySector === "Stablecoin";
      return (
        currency.currencyConfig?.permissions?.sell &&
        currency.currencyConfig?.type === "Crypto" &&
        !isStableCoin
      );
    });

    // map through permissible holdings and format as ICurrency objects
    return permissibleHoldings.map((currency): ICurrency => {
      const config = currency.currencyConfig;
      return {
        userBaseCurrency: userBaseCurrency,
        code: config.code ?? "",
        displayName: config.displayName ?? "",
        logoURL: config.logoUrl ?? "",
        units: currency.available.toString(),
        value: currency.value.toString(),
        rank: "",
        sector: "",
        price: currency.price,
      };
    });
  }, [userHoldings, userBaseCurrency]);

  /**
   * Memoized array of user's fiat holdings filtered by permissible currencies and formatted as ICurrency objects.
   */
  const usersFiatHoldings = useMemo((): ICurrency[] => {
    if (!userHoldings) return [];

    // remove user holdings that DO NOT have buy or sell permissions
    const permissibleHoldings = userHoldings.filter((currency) => {
      return currency.currencyConfig?.permissions?.sell && currency.currencyConfig?.type === "Fiat";
    });

    // map through permissible holdings and format as ICurrency objects
    return permissibleHoldings.map((currency): ICurrency => {
      const config = currency.currencyConfig;
      return {
        userBaseCurrency: userBaseCurrency,
        code: config.code ?? "",
        displayName: config.displayName ?? "",
        logoURL: config.logoUrl ?? "",
        units: currency.available.toString(),
        value: currency.value.toString(),
        rank: "",
        sector: "",
        price: currency.price,
      };
    });
  }, [userHoldings, userBaseCurrency]);

  /**
   * Memoized array of user's fiat and stable holdings filtered by permissible currencies and formatted as ICurrency objects.
   * Includes both fiat and stablecoins.
   */
  const userFiatAndStableHoldings = useMemo((): ICurrency[] => {
    if (!userHoldings) return [];

    // remove user holdings that DO NOT have buy or sell permissions
    const permissibleHoldings = userHoldings.filter((currency) => {
      const isStableCoin =
        currency.currencyConfig?.primarySector === "Stablecoin" ||
        currency.currencyConfig?.secondarySector === "Stablecoin";
      return (
        currency.currencyConfig?.permissions?.sell &&
        (currency.currencyConfig?.type === "Fiat" || isStableCoin)
      );
    });

    // map through permissible holdings and format as ICurrency objects
    return permissibleHoldings.map((currency): ICurrency => {
      const config = currency.currencyConfig;
      return {
        userBaseCurrency: userBaseCurrency,
        code: config.code ?? "",
        displayName: config.displayName ?? "",
        logoURL: config.logoUrl ?? "",
        units: currency.available.toString(),
        value: currency.value.toString(),
        rank: "",
        sector: "",
        price: currency.price,
      };
    });
  }, [userHoldings, userBaseCurrency]);

  /**
   * Memoized array of all user holdings filtered by permissible currencies and formatted as ICurrency objects.
   */
  const allUserHoldings = useMemo((): ICurrency[] => {
    if (!userHoldings) return [];

    // remove user holdings that DO NOT have buy or sell permissions
    const permissibleHoldings = userHoldings.filter((currency) => {
      return currency.currencyConfig?.permissions?.sell;
    });

    // map through permissible holdings and format as ICurrency objects
    return permissibleHoldings.map((currency): ICurrency => {
      const config = currency.currencyConfig;
      return {
        userBaseCurrency: userBaseCurrency,
        code: config.code ?? "",
        displayName: config.displayName ?? "",
        logoURL: config.logoUrl ?? "",
        units: currency.available.toString(),
        value: currency.value.toString(),
        rank: "",
        sector: "",
        price: currency.price,
      };
    });
  }, [userHoldings, userBaseCurrency]);

  /**
   * Memoized array of all cryptocurrencies filtered by permissible currencies and formatted as ICurrency objects.
   */
  const cryptoCurrencies = useMemo((): ICurrency[] => {
    if (!cryptoAll) return [];

    // remove cryptos that DO NOT have buy or sell permissions
    const permissiableCryptos = cryptoAll.filter((currency) => {
      return currency.permissions?.buy && !isTokenRestrictedForCountry(currency, userCountry);
    });

    // map through permissible cryptos and format as ICurrency objects
    return permissiableCryptos.map((currency): ICurrency => {
      return {
        userBaseCurrency: userBaseCurrency,
        code: currency.code ?? "",
        displayName: currency.displayName ?? "",
        logoURL: currency.logoUrl ?? "",
        units: "0",
        value: "0",
        rank: "",
        sector: "",
        price: currency.price ?? 0,
      };
    });
  }, [cryptoAll, userCountry]);

  /**
   * Memoized array of all forex currencies filtered by permissible currencies and formatted as ICurrency objects.
   */
  const forexCurrencies = useMemo((): ICurrency[] => {
    if (!forexAll) return [];

    // remove forex that DO NOT have buy or sell permissions
    const permissiableforex = forexAll.filter((currency) => {
      return currency.permissions?.buy;
    });

    // map through permissible forex and format as ICurrency objects
    return permissiableforex.map((currency): ICurrency => {
      return {
        userBaseCurrency: userBaseCurrency,
        code: currency.code ?? "",
        displayName: currency.name ?? "",
        logoURL: currency.logoUrl ?? "",
        units: "0",
        value: "0",
        rank: "",
        sector: "",
        price: currency.price ?? 0,
      };
    });
  }, [forexAll]);

  /**
   * Memoized array of cryptocurrencies and forexes combined with user's holdings, matching by currency code.
   */
  const allCurrenciesWithUserHoldings = useMemo((): ICurrency[] => {
    if (!cryptoCurrencies || !forexCurrencies) return [];

    // Helper function to combine currency data with user's holdings
    const combineWithUserHoldings = (currencies: ICurrency[]): ICurrency[] => {
      return currencies.map((currency): ICurrency => {
        const userCurrency = usersCryptoHoldings.find(
          (userCurrency): boolean => userCurrency.code === currency.code,
        );
        return userCurrency
          ? { ...currency, units: userCurrency.units, value: userCurrency.value }
          : currency;
      });
    };

    const combinedCryptos = combineWithUserHoldings(cryptoCurrencies);
    const combinedForexes = combineWithUserHoldings(forexCurrencies);

    return [...combinedCryptos, ...combinedForexes];
  }, [cryptoCurrencies, forexCurrencies, usersCryptoHoldings]);

  /**
   * Memoized array of cryptocurrencies combined with user's holdings, matching by currency code.
   * Excludes stablecoins and fiat currencies.
   */
  const cryptoNoStablesOrFiatWithUserHoldings = useMemo((): ICurrency[] => {
    if (!cryptoCurrencies) return [];

    const nonStables = cryptoCurrencies.filter((currency) => {
      const nonStable = !STABLE_COIN_CODES.includes(currency.code);

      return nonStable;
    });

    // Helper function to combine currency data with user's holdings
    const combineWithUserHoldings = (currencies: ICurrency[]): ICurrency[] => {
      return currencies.map((currency): ICurrency => {
        const userCurrency = usersCryptoHoldings.find(
          (userCurrency): boolean => userCurrency.code === currency.code,
        );
        return userCurrency
          ? { ...currency, units: userCurrency.units, value: userCurrency.value }
          : currency;
      });
    };

    return combineWithUserHoldings(nonStables);
  }, [cryptoCurrencies, usersCryptoHoldings]);

  /**
   * Memoized array of fiat and stable currencies combined with user's holdings, matching by currency code.
   */
  const fiatAndStablesWithUserHoldings = useMemo((): ICurrency[] => {
    if (!forexCurrencies || !cryptoCurrencies) return [];

    const stableCoins = cryptoCurrencies.filter((currency) => {
      const isStableCoin = STABLE_COIN_CODES.includes(currency.code);

      return isStableCoin;
    });

    const allFiatAndStables = [...forexCurrencies, ...stableCoins];

    const combineWithUserHoldings = (currencies: ICurrency[]): ICurrency[] => {
      return currencies.map((currency): ICurrency => {
        const userCurrency = userFiatAndStableHoldings.find(
          (userCurrency): boolean => userCurrency.code === currency.code,
        );
        return userCurrency
          ? { ...currency, units: userCurrency.units, value: userCurrency.value }
          : currency;
      });
    };

    return combineWithUserHoldings(allFiatAndStables);
  }, [forexCurrencies, cryptoCurrencies, userFiatAndStableHoldings]);

  /**
   * Memoized array of user's crypto holdings filtered by permissible currencies and formatted as ICurrency objects.
   * Excludes stablecoins.
   */
  const userCryptoHoldingWithoutStables = useMemo((): ICurrency[] => {
    if (!userHoldings) return [];

    // remove user holdings that DO NOT have buy or sell permissions
    const permissiableHoldings = userHoldings.filter((currency) => {
      const isStableCoin =
        currency.currencyConfig?.primarySector === "Stablecoin" ||
        currency.currencyConfig?.secondarySector === "Stablecoin";
      return (
        currency.currencyConfig?.permissions?.sell &&
        currency.currencyConfig?.type === "Crypto" &&
        !isStableCoin
      );
    });

    // map through permissible holdings and format as ICurrency objects
    return permissiableHoldings.map((currency): ICurrency => {
      const config = currency.currencyConfig;
      return {
        userBaseCurrency: userBaseCurrency,
        code: config.code ?? "",
        displayName: config.displayName ?? "",
        logoURL: config.logoUrl ?? "",
        units: currency.available.toString(),
        value: currency.value.toString(),
        rank: "",
        sector: "",
        price: currency.price,
      };
    });
  }, [userHoldings, userBaseCurrency]);

  /**
   * Memoized array of cryptocurrencies and forexes combined with user's holdings, matching by currency code.
   */
  const cryptoCurrenciesWithUserHoldings = useMemo((): ICurrency[] => {
    if (!cryptoCurrencies || !forexCurrencies) return [];

    // Helper function to combine currency data with user's holdings
    const combineWithUserHoldings = (currencies: ICurrency[]): ICurrency[] => {
      return currencies.map((currency): ICurrency => {
        const userCurrency = usersCryptoHoldings.find(
          (userCurrency): boolean => userCurrency.code === currency.code,
        );
        return userCurrency
          ? { ...currency, units: userCurrency.units, value: userCurrency.value }
          : currency;
      });
    };

    const combinedCryptos = combineWithUserHoldings(cryptoCurrencies);
    const combinedForexes = combineWithUserHoldings(forexCurrencies);

    return [...combinedCryptos, ...combinedForexes];
  }, [cryptoCurrencies, forexCurrencies, usersCryptoHoldings]);

  return {
    // Raw data
    userHoldings,
    cryptoAll,
    forexAll,

    // Transformed data
    allUserHoldings,
    userCryptoHoldingsWithoutStables,
    usersCryptoHoldings,
    usersFiatHoldings,
    cryptoCurrencies,
    forexCurrencies,
    allCurrenciesWithUserHoldings,
    cryptoNoStablesOrFiatWithUserHoldings,
    fiatAndStablesWithUserHoldings,
    userFiatAndStableHoldings,
    userCryptoHoldingWithoutStables,
    cryptoCurrenciesWithUserHoldings,

    // Individual loading states
    isHoldingsLoading,
    isHoldingsFetching,
    isCryptoAllLoading,
    isFiatLoading,

    // Individual error states
    isHoldingsError,
    isCryptoAllError,
    isFiatError,

    // Aggregated states
    isLoading: isHoldingsLoading || isHoldingsFetching || isCryptoAllLoading || isFiatLoading,
    isError: isHoldingsError || isCryptoAllError || isFiatError,

    // Actions
    refetchWallet,
  };
};
